import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  X, 
  Filter,
  Archive,
  Star,
  Clock,
  Zap,
  Mail,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

interface SmartNotification {
  id: string;
  type: 'success' | 'warning' | 'info' | 'error' | 'urgent';
  category: 'system' | 'ticket' | 'project' | 'user' | 'security';
  title: string;
  body: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  source: string;
  relatedEntity?: {
    type: 'ticket' | 'project' | 'user' | 'system';
    id: string;
    name: string;
  };
  actions?: NotificationAction[];
  metadata?: Record<string, any>;
  expiresAt?: Date;
  starred?: boolean;
  groupId?: string;
}

interface NotificationAction {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'danger';
  action: () => void;
  icon?: React.ComponentType<any>;
}

interface NotificationGroup {
  id: string;
  title: string;
  notifications: SmartNotification[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
}

interface SmartNotificationSystemProps {
  userId: string;
  maxItems?: number;
  enableGrouping?: boolean;
  enableFiltering?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const SmartNotificationSystem: React.FC<SmartNotificationSystemProps> = ({
  userId,
  maxItems = 10,
  enableGrouping = true,
  enableFiltering = true,
  autoRefresh = true,
  refreshInterval = 30000
}) => {
  const [notifications, setNotifications] = useState<SmartNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<SmartNotification[]>([]);
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');

  // Enhanced mock data generator
  const generateMockNotifications = useCallback((): SmartNotification[] => {
    const notifications: SmartNotification[] = [
      {
        id: '1',
        type: 'urgent',
        category: 'security',
        title: 'Security Alert: Multiple Failed Login Attempts',
        body: 'Detected 5+ failed login attempts from IP 192.168.1.100 in the last 10 minutes.',
        timestamp: new Date(Date.now() - 2 * 60 * 1000),
        read: false,
        priority: 'urgent',
        source: 'Security Monitor',
        relatedEntity: { type: 'system', id: 'sec-001', name: 'Security System' },
        actions: [
          {
            id: 'block-ip',
            label: 'Block IP',
            type: 'danger',
            action: () => console.log('Block IP'),
            icon: AlertCircle
          },
          {
            id: 'view-logs',
            label: 'View Logs',
            type: 'secondary',
            action: () => console.log('View security logs')
          }
        ],
        starred: true,
        groupId: 'security-alerts'
      },
      {
        id: '2',
        type: 'success',
        category: 'system',
        title: 'ETL Job Completed Successfully',
        body: 'Daily data sync completed with 1,247 records processed. Data quality score: 98.5%',
        timestamp: new Date(Date.now() - 5 * 60 * 1000),
        read: false,
        priority: 'medium',
        source: 'ETL Engine',
        relatedEntity: { type: 'system', id: 'etl-001', name: 'Daily Sync Job' },
        actions: [
          {
            id: 'view-report',
            label: 'View Report',
            type: 'primary',
            action: () => console.log('View ETL report')
          }
        ],
        metadata: { records: 1247, quality: 98.5, duration: '00:12:34' }
      },
      {
        id: '3',
        type: 'warning',
        category: 'ticket',
        title: 'SLA Breach Warning',
        body: 'Ticket #CB-2025-001 is approaching SLA threshold (2h remaining).',
        timestamp: new Date(Date.now() - 10 * 60 * 1000),
        read: false,
        priority: 'high',
        source: 'SLA Monitor',
        relatedEntity: { type: 'ticket', id: 'CB-2025-001', name: 'Login Issues' },
        actions: [
          {
            id: 'assign-urgent',
            label: 'Mark Urgent',
            type: 'danger',
            action: () => console.log('Mark urgent'),
            icon: Zap
          },
          {
            id: 'view-ticket',
            label: 'View Ticket',
            type: 'primary',
            action: () => console.log('View ticket')
          }
        ],
        expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000),
        groupId: 'sla-warnings'
      },
      {
        id: '4',
        type: 'info',
        category: 'project',
        title: 'Weekly Standup Reminder',
        body: 'Team standup meeting starts in 15 minutes. Conference room B or Zoom link available.',
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        read: false,
        priority: 'medium',
        source: 'Calendar',
        relatedEntity: { type: 'project', id: 'proj-001', name: 'HiveCore v2.0' },
        actions: [
          {
            id: 'join-meeting',
            label: 'Join Meeting',
            type: 'primary',
            action: () => console.log('Join meeting')
          },
          {
            id: 'snooze',
            label: 'Snooze 5min',
            type: 'secondary',
            action: () => console.log('Snooze notification')
          }
        ]
      },
      {
        id: '5',
        type: 'info',
        category: 'ticket',
        title: 'New Support Ticket Created',
        body: 'Ticket #CB-2025-003 created via conversational AI: "Password reset not working"',
        timestamp: new Date(Date.now() - 20 * 60 * 1000),
        read: true,
        priority: 'low',
        source: 'Support Bridge',
        relatedEntity: { type: 'ticket', id: 'CB-2025-003', name: 'Password Reset Issue' },
        actions: [
          {
            id: 'auto-assign',
            label: 'Auto Assign',
            type: 'primary',
            action: () => console.log('Auto assign ticket')
          }
        ]
      }
    ];

    return notifications;
  }, []);

  useEffect(() => {
    const mockNotifications = generateMockNotifications();
    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter(n => !n.read).length);
  }, [generateMockNotifications]);

  useEffect(() => {
    // Apply filters
    let filtered = notifications;
    
    if (activeFilter !== 'all') {
      if (activeFilter === 'unread') {
        filtered = notifications.filter(n => !n.read);
      } else if (activeFilter === 'starred') {
        filtered = notifications.filter(n => n.starred);
      } else {
        filtered = notifications.filter(n => n.category === activeFilter);
      }
    }

    setFilteredNotifications(filtered);

    // Group notifications if enabled
    if (enableGrouping) {
      const groupMap = new Map<string, SmartNotification[]>();
      
      filtered.forEach(notification => {
        const groupKey = notification.groupId || notification.category;
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)!.push(notification);
      });

      const newGroups: NotificationGroup[] = Array.from(groupMap.entries()).map(([groupId, notifications]) => ({
        id: groupId,
        title: formatGroupTitle(groupId),
        notifications: notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
        priority: getGroupPriority(notifications),
        category: notifications[0]?.category || 'system'
      }));

      setGroups(newGroups.sort((a, b) => getPriorityWeight(b.priority) - getPriorityWeight(a.priority)));
    }
  }, [notifications, activeFilter, enableGrouping]);

  const formatGroupTitle = (groupId: string): string => {
    const titleMap: Record<string, string> = {
      'security-alerts': 'Security Alerts',
      'sla-warnings': 'SLA Warnings',
      'system': 'System Notifications',
      'ticket': 'Support Tickets',
      'project': 'Project Updates',
      'user': 'User Activities'
    };
    return titleMap[groupId] || groupId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getGroupPriority = (notifications: SmartNotification[]): 'low' | 'medium' | 'high' | 'urgent' => {
    const priorities = notifications.map(n => n.priority);
    if (priorities.includes('urgent')) return 'urgent';
    if (priorities.includes('high')) return 'high';
    if (priorities.includes('medium')) return 'medium';
    return 'low';
  };

  const getPriorityWeight = (priority: string): number => {
    const weights = { urgent: 4, high: 3, medium: 2, low: 1 };
    return weights[priority as keyof typeof weights] || 0;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'error': return AlertCircle;
      case 'urgent': return Zap;
      default: return Info;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-emerald-400';
      case 'warning': return 'text-amber-400';
      case 'error': 
      case 'urgent': return 'text-red-400';
      default: return 'text-cyan-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const dismissNotification = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const toggleStar = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, starred: !n.starred } : n)
    );
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  const filterOptions = [
    { id: 'all', label: 'All', count: notifications.length },
    { id: 'unread', label: 'Unread', count: unreadCount },
    { id: 'starred', label: 'Starred', count: notifications.filter(n => n.starred).length },
    { id: 'system', label: 'System', count: notifications.filter(n => n.category === 'system').length },
    { id: 'ticket', label: 'Tickets', count: notifications.filter(n => n.category === 'ticket').length },
    { id: 'security', label: 'Security', count: notifications.filter(n => n.category === 'security').length }
  ];

  const renderNotification = (notification: SmartNotification) => {
    const Icon = getTypeIcon(notification.type);
    
    return (
      <motion.div
        key={notification.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className={`p-4 rounded-lg border transition-all duration-200 hover:bg-slate-800/40 ${
          notification.read 
            ? 'control-bridge-glass-dark opacity-70' 
            : 'control-bridge-glass-dark border-slate-600/50'
        } ${notification.priority === 'urgent' ? 'ring-1 ring-red-500/50' : ''}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon className={`w-4 h-4 ${getTypeColor(notification.type)}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className={`text-sm font-medium ${
                    notification.read ? 'text-slate-300' : 'text-white'
                  }`}>
                    {notification.title}
                  </h4>
                  {notification.priority !== 'low' && (
                    <Badge className={`text-xs px-1.5 py-0.5 ${getPriorityColor(notification.priority)}`}>
                      {notification.priority}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 mb-2">
                  {notification.body}
                </p>
              </div>
              
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleStar(notification.id)}
                  className={`h-6 w-6 p-0 ${
                    notification.starred 
                      ? 'text-yellow-400 hover:text-yellow-300' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Star className={`w-3 h-3 ${notification.starred ? 'fill-current' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissNotification(notification.id)}
                  className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-3">
                <span className="text-slate-500">{formatTimestamp(notification.timestamp)}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{notification.source}</span>
                {notification.relatedEntity && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span className="text-cyan-400">{notification.relatedEntity.name}</span>
                  </>
                )}
              </div>
              
              {!notification.read && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markAsRead(notification.id)}
                  className="h-5 px-2 text-xs text-slate-400 hover:text-white"
                >
                  Mark read
                </Button>
              )}
            </div>

            {notification.actions && notification.actions.length > 0 && (
              <div className="flex gap-2 mt-3">
                {notification.actions.map((action) => (
                  <Button
                    key={action.id}
                    variant="ghost"
                    size="sm"
                    onClick={action.action}
                    className={`h-6 px-2 text-xs ${
                      action.type === 'primary' ? 'text-cyan-400 hover:text-cyan-300' :
                      action.type === 'danger' ? 'text-red-400 hover:text-red-300' :
                      'text-slate-400 hover:text-white'
                    }`}
                  >
                    {action.icon && <action.icon className="w-3 h-3 mr-1" />}
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Card className="control-bridge-glass-dark rounded-2xl">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Smart Notifications
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {unreadCount}
            </Badge>
          )}
        </CardTitle>
        
        <div className="flex items-center gap-2">
          {enableFiltering && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-slate-400 hover:text-white"
            >
              <Filter className="w-4 h-4" />
            </Button>
          )}
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-slate-400 hover:text-white"
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      {enableFiltering && showFilters && (
        <div className="px-6 pb-4">
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <Button
                key={option.id}
                variant="ghost"
                size="sm"
                onClick={() => setActiveFilter(option.id)}
                className={`text-xs px-3 py-1 ${
                  activeFilter === option.id
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                {option.label}
                {option.count > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 text-xs">
                    {option.count}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      <CardContent className="space-y-3 max-h-96 overflow-y-auto">
        <AnimatePresence>
          {viewMode === 'grouped' && enableGrouping ? (
            groups.slice(0, maxItems).map((group) => (
              <div key={group.id} className="space-y-2">
                <div className="flex items-center gap-2 px-2">
                  <h5 className="text-sm font-medium text-slate-300">{group.title}</h5>
                  <Badge className={`text-xs ${getPriorityColor(group.priority)}`}>
                    {group.notifications.length}
                  </Badge>
                </div>
                {group.notifications.slice(0, 3).map(renderNotification)}
                {group.notifications.length > 3 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-slate-400 hover:text-white"
                  >
                    Show {group.notifications.length - 3} more...
                  </Button>
                )}
              </div>
            ))
          ) : (
            filteredNotifications.slice(0, maxItems).map(renderNotification)
          )}
        </AnimatePresence>
        
        {filteredNotifications.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notifications</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
